import React from "react";
import { useDispatch } from "react-redux";
import {
  CreateAssetResponse,
  FileCardOptionsTypeEnum,
} from "app/components/MediaUploader/types";
import { ticketDetailsActions } from "app/containers/Features/containers/Ticket/redux/slice";
import { useAttachmentPreviewController } from "app/containers/Features/containers/Ticket/components/Content/components/Details/components/FileUploadSection/hooks/useAttachmentPreviewController";
import { copyTextToClipboard } from "app/containers/Features/containers/Ticket/components/AttachmentPreview/helper/copyTextToClipboard";
import { downloadRemoteAttachment } from "app/containers/Features/containers/Ticket/components/AttachmentPreview/helper/downloadRemoteAttachment";
import type { TicketAttachmentFile } from "app/containers/Features/containers/Ticket/types";
import { ITicketFormData } from "../../../containers/Tickets/types";
import { ticketGroupsActions } from "../../../containers/Tickets/redux/slice";

export const useAddTaskAttachments = (form: ITicketFormData) => {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = React.useState(false);
  const [previewFileId, setPreviewFileId] = React.useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [renamingFileId, setRenamingFileId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState("");

  const files: TicketAttachmentFile[] = React.useMemo(
    () =>
      (form.file || []).map((file) => ({
        id: file._id,
        name: file.fileName || "Attachment",
        type: file.type,
        createdAt: file.createdAt,
        url: file.url,
      })),
    [form.file],
  );

  const uploadingCards = isUploading
    ? [{ id: "uploading", name: "Uploading file..." }]
    : [];

  const updateFiles = React.useCallback(
    (nextFiles: CreateAssetResponse[]) => {
      dispatch(ticketGroupsActions.setTicketFormValue({ name: "file", value: nextFiles }));
      dispatch(
        ticketGroupsActions.setTicketFormValue({
          name: "attachments",
          value: nextFiles.map((file) => file._id),
        }),
      );
    },
    [dispatch],
  );

  const clearRename = React.useCallback(() => {
    setRenamingFileId(null);
    setRenameValue("");
  }, []);

  const renameFile = React.useCallback(
    (fileId: string, trimmedName: string, onComplete?: () => void) => {
      dispatch(
        ticketDetailsActions.requestRenameAttachment({
          assetId: fileId,
          fileName: trimmedName,
          onRenamed: () => {
            const nextFiles = (form.file || []).map((sourceFile) =>
              sourceFile._id === fileId
                ? { ...sourceFile, fileName: trimmedName }
                : sourceFile,
            );
            updateFiles(nextFiles);
            onComplete?.();
          },
        }),
      );
    },
    [dispatch, form.file, updateFiles],
  );

  const submitRename = React.useCallback(() => {
    if (!renamingFileId) return;
    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      clearRename();
      return;
    }
    const currentFile = files.find((f) => f.id === renamingFileId);
    if (currentFile?.name === trimmedName) {
      clearRename();
      return;
    }
    renameFile(renamingFileId, trimmedName, clearRename);
  }, [clearRename, files, renameFile, renameValue, renamingFileId]);

  const handleAttachmentAction = React.useCallback(
    (type: FileCardOptionsTypeEnum, file: TicketAttachmentFile) => {
      const sourceFiles = form.file || [];
      const matched = sourceFiles.find((sourceFile) => sourceFile._id === file.id);

      switch (type) {
        case FileCardOptionsTypeEnum.COPY_URL:
          if (matched?.url) {
            void copyTextToClipboard(matched.url);
          }
          break;
        case FileCardOptionsTypeEnum.DOWNLOAD:
          if (matched?.url) {
            void downloadRemoteAttachment(
              matched.url,
              matched.fileName || file.name || "attachment",
            );
          }
          break;
        case FileCardOptionsTypeEnum.IS_REMOVE: {
          const nextFiles = sourceFiles.filter(
            (sourceFile) => sourceFile._id !== file.id,
          );
          updateFiles(nextFiles);
          if (previewFileId === file.id) {
            setIsPreviewOpen(false);
            setPreviewFileId(null);
          }
          break;
        }
        case FileCardOptionsTypeEnum.IS_EDIT:
          setRenamingFileId(file.id);
          setRenameValue(file.name);
          break;
        default:
          break;
      }
    },
    [form.file, previewFileId, updateFiles],
  );

  const preview = useAttachmentPreviewController({
    files,
    previewFileId,
    previewFileIds: null,
    isPreviewOpen,
    onPreviewFileChange: setPreviewFileId,
    onPreviewOpenChange: setIsPreviewOpen,
    onAttachmentAction: handleAttachmentAction,
    onRenameSubmit: (file, nextName) => {
      const trimmedName = nextName.trim();
      if (!trimmedName) return;
      renameFile(file.id, trimmedName);
    },
  });

  return {
    files,
    uploadingCards,
    setIsUploading,
    renamingFileId,
    renameValue,
    setRenameValue,
    submitRename,
    clearRename,
    handleAttachmentAction,
    ...preview,
  };
};

export default useAddTaskAttachments;
